import React, {
  useContext,
  useEffect,
  useState,
} from 'react';
import SubscribableState from 'app/utils/SubscribableState';

/**
 * `createStateContext()` allows you to access the state of a
 * `SubscribableState` instance via React context.
 *
 * This is intended to allow writing and testing the logic outside of React,
 * while still being able to use it from React components easily.
 *
 * For this purpose, it is recommended to write a module that exports separate
 * functions that take the SubscribableState instance as an argument and help
 * access or update the state. This approach has the added benefit of allowing
 * more dead code elimination via Webpack's tree shaking.
 *
 * To use the state from React components, you can use `useStore` or
 * `useStoreNoSubscribe` directly, and then pass the returned SubscribableState
 * instance to your helper functions which abstract the logic.
 */
export default function createStateContext<StateType>() {
  const StateContext = React.createContext<StateType | undefined>(undefined);
  const StoreContext = React.createContext<SubscribableState<StateType> | undefined>(undefined);

  /**
   * Returns the SubscribableState state instance, and causes a re-render
   * whenever its state changes.
   */
  const useStore = () => {
    // Used for side effect of causing re-renders when state changes.
    useContext(StateContext);

    return useContext(StoreContext);
  };
  /**
   * Same as `useStore`, but does not cause a re-render when the state changes.
   */
  const useStoreNoSubscribe = () => useContext(StoreContext);

  const Provider: React.FC<{
    store: SubscribableState<StateType>,
  }> = ({
    children,
    store,
  }) => {
    const [storeState, setStoreState] = useState(() => store.state);

    useEffect(() => {
      const updateState = () => setStoreState(store.state);

      const unsubscribe = store.subscribe(updateState);

      // The state might have changed between first render and first call to useEffect, before we
      // had a chance to call `.subscribe()`
      //
      // If the value hasn't changed, this will not cause a re-render
      updateState();

      return unsubscribe;
    }, [store]);

    return (
      <StoreContext.Provider value={store}>
        <StateContext.Provider value={storeState}>
          {children}
        </StateContext.Provider>
      </StoreContext.Provider>
    );
  };

  return { Provider, useStore, useStoreNoSubscribe };
}
